import { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { datadogRum } from '@datadog/browser-rum';
import CONFIG from "../config/config";
import ReferralsAPI from "../api/ReferralsAPI";
import { toastService } from "../services/ToastService";

export const useReferral = (shouldFetch: boolean = false) => {
  const { t, i18n } = useTranslation();
  const [referralData, setReferralData] = useState<{ referralCode: string; referralsCount: number } | null>(null);
  const [isLoadingReferralData, setIsLoadingReferralData] = useState(false);

  const referralCode = referralData?.referralCode || '';
  
  const getReferralLandingUrl = (language: string): string => {
    const languageMap: Record<string, string> = {
      'es': 'referral-es.html',
      'de': 'referral-de.html',
      'pl': 'referral-pl.html',
    };
    
    const referralPage = languageMap[language] || 'referral.html';
    return `${CONFIG.MAIN_WEBSITE}/${referralPage}`;
  };

  const referralLanding = useMemo(
    () => getReferralLandingUrl(i18n.language),
    [i18n.language]
  );

  const referralLink = useMemo(
    () => `${window.location.origin}/onboarding?refCode=${encodeURIComponent(referralCode)}`,
    [referralCode]
  );
  const referredStoresCount = referralData?.referralsCount ?? 0;

  useEffect(() => {
    if (shouldFetch) {
      setIsLoadingReferralData(true);
      ReferralsAPI.get()
        .then((response) => {
          setReferralData(response.data);
        })
        .catch((error) => {
          console.error("Failed to fetch referral data:", error);
          toastService.showToast(
            t("referrals.error.fetch"),
            "error"
          );
        })
        .finally(() => {
          setIsLoadingReferralData(false);
        });
    }
  }, [shouldFetch, t]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      datadogRum.addAction('Copy Referral Link', {
        success: true,
        referralCode: referralCode || 'unknown'
      });
      toastService.showToast(
        t("referrals.toast.copied")
      );
    } catch {
      toastService.showToast(
        t("referrals.toast.error"),
        "error"
      );
    }
  };

  return {
    referralCode,
    referralLink,
    referralLanding,
    referredStoresCount,
    isLoadingReferralData,
    handleCopy,
  };
};

